import React, { useEffect, useRef, useState } from "react";
import {
  generatePreviewURLForBrand,
  generatePreviewURLForSor,
} from "../../../utils/commonFunction";
import {
  UploadActions,
  UploadBox,
  UploadPlaceholderIcon,
  UploadPlaceholderText,
  UploadPreviewImage,
} from "./CourseGradeSettings.styles";

const PREVIEW_URL_BY_TYPE = {
  sor: generatePreviewURLForSor,
  brand: generatePreviewURLForBrand,
};

/**
 * Checks whether a selected file matches the configured media type.
 * @param {File} file Selected file
 * @param {boolean} isVideo Whether the field accepts video
 * @returns {boolean} Whether the file matches
 */
function isAcceptedFile(file, isVideo) {
  if (isVideo) {
    return file?.type?.startsWith("video/");
  }

  return file?.type?.startsWith("image/") || /\.svg$/i.test(file?.name || "");
}

/**
 * Renders an image/video upload bound to a Formik field.
 * @param {object} props Component properties
 * @param {string} props.label Field label
 * @param {string} props.fieldName Formik field name
 * @param {object} props.formik Formik instance
 * @param {string|string[]} props.accept Accepted file types
 * @param {boolean} props.isVideo Whether the field accepts video
 * @param {boolean} props.required Whether the field is required
 * @param {boolean} props.showRemove Whether to show the remove button
 * @param {string} props.className Layout classes
 * @param {"sor"|"brand"} props.previewType Stored-file preview URL strategy
 * @returns {React.ReactElement} Image upload field
 */
export default function ImageUploadField({
  label,
  fieldName,
  formik,
  accept = "image/*,.svg",
  isVideo = false,
  required = false,
  showRemove = true,
  className = "col-md-6 col-lg-4 mb-4 my-4",
  previewType = "sor",
}) {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const value = formik.values[fieldName];
  const validationError =
    formik.touched[fieldName] && formik.errors[fieldName]
      ? formik.errors[fieldName]
      : "";
  const hasError = Boolean(validationError);
  const generatePreviewUrl = PREVIEW_URL_BY_TYPE[previewType] || generatePreviewURLForSor;
  const fileName = value instanceof File
    ? value.name
    : value?.split("/").pop() || "";

  useEffect(() => {
    if (!value) {
      setPreviewUrl("");
      return undefined;
    }

    if (value instanceof File) {
      const objectUrl = URL.createObjectURL(value);
      setPreviewUrl(objectUrl);

      return () => {
        URL.revokeObjectURL(objectUrl);
      };
    }

    setPreviewUrl(generatePreviewUrl(value));
    return undefined;
  }, [generatePreviewUrl, value]);

  /**
   * Validates and stores the selected file on the Formik field.
   * @param {File} file Selected image
   */
  const selectImage = (file) => {
    if (!file) {
      return;
    }

    if (!isAcceptedFile(file, isVideo)) {
      // skip Yup so this message stays until the next field change/submit
      formik.setFieldTouched(fieldName, true, false);
      formik.setFieldError(
        fieldName,
        `Please select a ${isVideo ? "video" : "image"} file`
      );
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    formik.setFieldValue(fieldName, file);
  };

  /**
   * Updates the drag-over state without allowing browser navigation.
   * @param {React.DragEvent} event Drag event
   */
  const handleDrag = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(event.type === "dragenter" || event.type === "dragover");
  };

  /**
   * Handles an image dropped onto the preview box.
   * @param {React.DragEvent} event Drop event
   */
  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    selectImage(event.dataTransfer.files?.[0]);
  };

  /**
   * Removes the current image and resets the native file input.
   */
  const handleRemove = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    formik.setFieldValue(fieldName, "");
  };

  return (
    <div className={className}>
      <label htmlFor={fieldName}>
        {label} {required && <span className="required-star">*</span>}
      </label>
      <UploadBox
        $dragActive={dragActive}
        $hasError={hasError}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        {previewUrl ? (
          <UploadPreviewImage
            as={isVideo ? "video" : "img"}
            src={previewUrl}
            alt={isVideo ? undefined : `${label} preview`}
            controls={isVideo || undefined}
          />
        ) : (
          <>
            <UploadPlaceholderIcon
              className={isVideo ? "fal fa-file-video" : "fal fa-image"}
            />
            <UploadPlaceholderText>Drag and Drop</UploadPlaceholderText>
          </>
        )}
      </UploadBox>

      {previewUrl && (
        <div className="frm-group assest-custom">
          <ul className="list-unstyled attached-file-ul m-0">
            <li>
              <a className="mr-0" href={previewUrl} target="_blank" rel="noreferrer">
                <i className={isVideo ? "fal fa-file-video" : "fal fa-file"}></i>
                &nbsp;{fileName}
              </a>
              {value && showRemove && (
                <button
                  type="button"
                  className="btn btn-danger btn-sm ml-2 rounded-circle"
                  title={`Remove ${label}`}
                  onClick={handleRemove}
                >
                  <i className="fal fa-trash-alt"></i>
                </button>
              )}
            </li>
          </ul>
        </div>
      )}

      <UploadActions>
        <label
          htmlFor={fieldName}
          className={
            "btn btn-save btn-primary btn-sm px-3 py-2 d-inline-flex align-items-center mb-0 cursor-pointer" +
            (hasError ? " file-req is-invalid" : "")
          }
        >
          <i className="fal fa-paperclip mr-2"></i>
          Add Attachment
        </label>
        <input
          ref={fileInputRef}
          type="file"
          id={fieldName}
          name={fieldName}
          className="d-none"
          accept={Array.isArray(accept) ? accept.join(",") : accept}
          onChange={(event) => selectImage(event.target.files?.[0])}
        />
      </UploadActions>
    </div>
  );
}
